export type Role = 'SYS_ADMIN' | 'ADMIN' | 'ENUMERATOR';

export const dv = {
  appName: 'WapStat',
  common: {
    save: 'Save', cancel: 'Cancel', edit: 'Edit', delete: 'Delete',
    loading: 'Loading…', back: 'Back', close: 'Close', submit: 'Submit',
    add: 'Add', create: 'Create', remove: 'Remove', search: 'Search…',
    download: 'Download', downloading: 'Downloading…', error: 'Something went wrong. Please try again.',
    required: 'This field is required', none: 'None', confirm: 'Confirm',
    active: 'Active', inactive: 'Deactivated', suspended: 'Suspended',
    page: 'Page', next: 'Next', prev: 'Previous', of: 'of', saving: 'Saving…', saved: 'Saved',
  },
  auth: {
    login: 'Sign in', email: 'Email', password: 'Password',
    forgotPassword: 'Forgot your password?',
    resetPassword: 'Reset password', newPassword: 'New password',
    confirmPassword: 'Confirm password', passwordMismatch: 'Passwords do not match',
    invalidCredentials: 'Incorrect email or password',
    logout: 'Log out', yourName: 'Full name',
    acceptInviteTitle: 'Create account', acceptInvite: 'Create account',
    resetSent: 'We have emailed you a link to reset your password',
    sendResetLink: 'Send link',
    resetLinkExpired: 'This link has expired. Please request a new one.',
    profile: 'Profile', changePassword: 'Change password',
    currentPassword: 'Current password', passwordChanged: 'Password changed',
    saved: 'Saved',
    showPassword: 'Show password', hidePassword: 'Hide password',
    signingIn: 'Signing in…',
    handoffInvalid: 'This link is invalid or has expired',
  },
  nav: {
    dashboard: 'Dashboard', houses: 'Houses', dhaftharu: 'Registers',
    users: 'Users',
    orgs: 'Councils', notifications: 'Notifications', profile: 'Profile',
    settings: 'Settings',
  },
  roles: {
    SYS_ADMIN: 'System admin',
    ADMIN: 'Admin',
    ENUMERATOR: 'Enumerator',
  } satisfies Record<Role, string>,
  orgAdminBadge: 'Admin',
  orgs: {
    title: 'Councils', create: 'New council', name: 'Council name',
    contactEmail: 'Contact email', users: 'Users', created: 'Created',
    inviteAdmin: 'Invite admin', adminRole: 'Admin role',
    suspend: 'Suspend', activate: 'Activate',
    inviteSent: 'Invitation emailed', empty: 'No councils yet',
  },
  users: {
    title: 'Users', invite: 'Invite a user', name: 'Name',
    role: 'Role', status: 'Status', orgAdmin: 'Admin access',
    designationDv: 'Designation (Dhivehi)', designationEn: 'Designation (English)',
    editDesignation: 'Edit designation',
    deactivate: 'Deactivate', activate: 'Activate',
    pendingInvites: 'Pending invitations', revokeInvite: 'Revoke invitation',
    inviteSent: 'Invitation emailed', expires: 'Expires',
    empty: 'No users yet',
    sendReset: 'Send password reset link',
    resetShort: 'Reset password',
    resetSent: 'Reset link emailed',
    managedInOffice: 'Users are managed in Wapplify Office',
    managedInOfficeHint: 'Adding users, changing roles, and activating/deactivating are done in Wapplify Office',
    nameInOffice: 'Managed in Wapplify Office',
    passwordInOffice: 'Passwords are changed in Wapplify Office',
    passwordInOfficeHint: 'Open Wapplify Office to change the password',
    openOffice: 'Open Office',
    breakGlassNote: 'Users are managed primarily in Wapplify Office. This screen is for special cases only.',
  },
  houses: {
    title: 'Houses',
    search: 'Search a house…',
    add: 'Add a house',
    name: 'House name',
    number: 'House number',
    numberShort: 'No.',
    ward: 'Ward',
    address: 'Address',
    empty: 'No houses yet',
    bulkLoad: 'Bulk load',
    bulkHint: 'Enter each house on a separate line',
    bulkPlaceholder: 'House name\nAnother house\n…',
    bulkSubmit: 'Add all houses',
    bulkResult: (created: number, skipped: number) => `${created} houses added · ${skipped} skipped`,
    openRegister: 'Open register',
  },
  register: {
    title: 'House register',
    // People
    persons: 'People',
    addPerson: 'Add a person',
    addPersonName: 'Person name',
    noPersons: 'No people yet',
    openPerson: 'Details',
    backToHouse: 'Back to house',
    // Person fields
    fullName: 'Full name',
    fullNameRequired: 'Full name is required',
    nationalId: 'National ID',
    dateOfBirth: 'Date of birth',
    age: 'Age',
    ageYears: (n: number) => `${n} years`,
    sex: 'Gender',
    sexLabel: { MALE: 'Male', FEMALE: 'Female' } as Record<string, string>,
    relationToHead: 'Relationship to head of house',
    maritalStatus: 'Marital status',
    contact: 'Contact',
    educationLevel: 'Education level',
    occupation: 'Occupation',
    health: 'Health info',
    notes: 'Notes',
    // Form section headings
    sectionIdentity: 'Basic information',
    sectionContact: 'Contact',
    sectionBackground: 'Education & occupation',
    sectionHealth: 'Health',
    sectionNotes: 'Notes',
    // Status
    status: 'Status',
    statusLabel: { ACTIVE: 'Living', MOVED_OUT: 'Moved away', DECEASED: 'Deceased' } as Record<string, string>,
    movedTo: 'Moved to', movedToPlaceholder: 'Type an island or Abroad…',
    movedDate: 'Moved date',
    deceasedDate: 'Deceased date',
    returnedDate: 'Returned date',
    apply: 'Apply',
    movedSummary: (location: string | null, date: string) =>
      `Moved to ${location || '—'} · ${date}`,
    deceasedSummary: (date: string) => `Deceased · ${date}`,
    returnedSummary: (date: string) => `Returned · ${date}`,
    // Status-history timeline
    statusHistory: 'Status history',
    tabDetails: 'Details',
    tabHistory: 'History',
    historyEmpty: 'No history yet',
    historyBy: (name: string) => `by ${name}`,
    historySearch: 'Search activity…',
    historyNoMatch: 'No matching activity',
    historyFilter: { all: 'All', status: 'Status', edits: 'Edits', transfers: 'Transfers' },
    historyLabel: {
      added: 'Added to register',
      movedAway: 'Moved away',
      movedAwayTo: (location: string) => `Moved away to ${location}`,
      returned: 'Returned',
      deceased: 'Deceased',
      transferred: 'Moved to another house',
      edited: 'Details updated',
    },
    // Move
    movePerson: 'Move to another house',
    moveTarget: 'Destination house',
    moved: 'Moved',
    notFound: 'House not found',
    personNotFound: 'Person not found',
    // House owner
    ownerBadge: 'Owner',
    markAsOwner: 'Mark as owner',
    removeOwner: 'Remove as owner',
    // Businesses
    businesses: 'Businesses',
    houseBusinesses: 'Registered businesses',
    homeBusinesses: 'Home businesses',
    addBusiness: 'Add a business',
    businessName: 'Business name',
    businessType: 'Business type',
    businessOwner: 'Owner',
    closeBusiness: 'Close',
    noBusinesses: 'No businesses yet',
    editBusiness: 'Edit business',
    customFields: 'Custom fields',
  },
  forms: {
    title: 'Forms',
    intro: 'Add a section, then add fields into it. Fields are collected for residents and businesses.',
    addSection: 'Add section',
    sectionName: 'Section name',
    renameSection: 'Rename section',
    deleteSection: 'Delete section',
    deleteSectionConfirm: 'Delete this section and all its fields?',
    noSections: 'No sections yet — add a section to start.',
    addField: 'Add field',
    editField: 'Edit field',
    deleteField: 'Delete field',
    deleteFieldConfirm: 'Delete this field?',
    label: 'Label',
    type: 'Type',
    required: 'Required',
    options: 'Options',
    addOption: 'Add option',
    optionPlaceholder: 'Option',
    optionsRequired: 'Add at least one option',
    moveUp: 'Move up',
    moveDown: 'Move down',
    requiredMark: 'Required',
    systemDefault: 'Default',
    typeLabel: {
      TEXT: 'Text',
      NUMBER: 'Number',
      SELECT: 'Dropdown',
      CHOICE: 'Choice',
      MULTISELECT: 'Multi-select',
      DATE: 'Date',
    } as Record<string, string>,
    entityLabel: {
      RESIDENT: 'Residents',
      REGISTERED_BUSINESS: 'Registered businesses',
      HOME_BUSINESS: 'Home businesses',
    } as Record<string, string>,
  },
  settings: {
    title: 'Settings',
    tabUsers: 'Users',
    tabForms: 'Forms',
  },
  dhaftharu: {
    title: 'Registers',
    create: 'New register',
    titleField: 'Register title',
    description: 'Description',
    entries: 'Entries',
    empty: 'No registers yet',
    delete: 'Delete register',
    deleteConfirm: 'Delete this register?',
    searchResidents: 'Search a resident…',
    noResults: 'No results',
    addEntry: 'Add',
    removeEntry: 'Remove',
    moveUp: 'Move up',
    moveDown: 'Move down',
    noEntries: 'No entries yet',
    generate: 'Generate PDF',
    generating: 'Generating…',
    generateQueued: 'PDF generation has been queued. Please download in a moment.',
    download: 'Download',
    notGenerated: 'No PDF generated yet',
    // Register kind
    kindLabel: 'Register type',
    kindManual: 'Manual register',
    kindConditional: 'Conditional (smart) register',
    kindHelp: 'Manual — add residents by hand. Conditional — automatically list everyone who matches conditions you define.',
    smart: 'Smart',
    // Conditions builder
    conditions: 'Conditions',
    addCondition: 'Add condition',
    noConditions: 'No conditions yet — add one to filter residents.',
    // Column picker
    columnsSection: 'Columns',
    addColumn: 'Add column',
    noColumns: 'No columns configured — the PDF will use default columns.',
    pickColumnPlaceholder: 'Add a column…',
    // Settings panel (conditions + columns)
    settingsHeading: 'Register settings',
    settingsConditionsCount: (n: number): string => `${n} ${n === 1 ? 'condition' : 'conditions'}`,
    settingsColumnsCount: (n: number): string => `${n === 0 ? 'default' : n} columns`,
    // Preview table
    previewHeading: 'Live preview',
    noPreviewRows: 'No residents match these conditions yet.',
    // Shared group labels
    groupResident: 'Resident',
    groupRegisteredBiz: 'Registered business',
    groupHomeBiz: 'Home business',
    groupHouse: 'House',
    // Column source display labels
    colName: 'Name',
    colStatus: 'Status',
    // Operator value helpers
    andLabel: 'and',
    // Operator labels
    opContains: 'Contains',
    opEquals: 'Is',
    opFilled: 'Is filled',
    opEmpty: 'Is empty',
    opEq: '=',
    opNe: '≠',
    opLt: '<',
    opLte: '≤',
    opGt: '>',
    opGte: '≥',
    opBetween: 'Is between',
    opIn: 'Is any of',
    opNotIn: 'Is not any of',
    opIncludesAny: 'Includes any of',
    opIncludesAll: 'Includes all of',
    opBefore: 'Before',
    opAfter: 'After',
    opOn: 'On',
    // Manual kind — columns section
    manualColumnsHeading: 'Column configuration',
    saveColumns: 'Save columns',
    // Save guard
    incompleteConditions: 'Finish or remove every condition before saving.',
  },
  notifications: {
    title: 'Notifications', empty: 'No notifications', markAllRead: 'Mark all as read',
  },
  dashboard: {
    welcome: 'Welcome',
    quickLinks: 'Quick links',
    housesLink: 'Houses',
    dhaftharuLink: 'Registers',
    viewHouses: 'Go to houses',
  },
  notFound: { title: 'Page not found', home: 'Back to home' },
} as const;
